import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { type EntityRef, Status } from "@client/types";
import { type AppState } from "@client/types/AppState";
import { type AnyAction, type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { GET_COMMENTS_PAGE_ARGS } from "./lib";
import { localStateSelector } from "./selectors";
import { type BlogDetailComments } from "./types";

export enum Actions {
  InitBegin = "MODULE_BLOG_DETAIL_COMMENTS_INIT_BEGIN",
  InitFailure = "MODULE_BLOG_DETAIL_COMMENTS_INIT_FAILURE",
  InitSuccess = "MODULE_BLOG_DETAIL_COMMENTS_INIT_SUCCESS",

  AddCommentBegin = "MODULE_BLOG_DETAIL_COMMENTS_ADD_COMMENT_BEGIN",
  AddCommentFailure = "MODULE_BLOG_DETAIL_COMMENTS_ADD_COMMENT_FAILURE",
  AddCommentSuccess = "MODULE_BLOG_DETAIL_COMMENTS_ADD_COMMENT_SUCCESS",

  LoadMoreBegin = "MODULE_BLOG_DETAIL_COMMENTS_LOAD_MORE_BEGIN",
  LoadMoreFailure = "MODULE_BLOG_DETAIL_COMMENTS_LOAD_MORE_FAILURE",
  LoadMoreSuccess = "MODULE_BLOG_DETAIL_COMMENTS_LOAD_MORE_SUCCESS"
}

export type AddCommentBeginData = BlogDetailComments.View.AddCommentFormData & EntityRef;
export type AddCommentSuccessData = BlogDetailComments.Api.AddCommentResponse;
export const addComment = apiAction<
  AppState,
  AddCommentBeginData,
  AddCommentSuccessData,
  BlogDetailComments.Api.AddCommentResponse
>({
  types: {
    begin: Actions.AddCommentBegin,
    failure: Actions.AddCommentFailure,
    success: Actions.AddCommentSuccess
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: AddCommentBeginData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const localState = localStateSelector(state, args);

    return api.addComment(apiInfo, {
      postId: localState.postId,
      comment: args.comment,
      email: args.email,
      name: args.name
    });
  }
});

export type InitBeginActionData = EntityRef & {
  page: number;
  postId: string;
};
export type InitSuccessActionData = BlogDetailComments.Api.InitResponse & {
  hasMore: boolean;
};
export const _init = initAction<
  AppState,
  InitBeginActionData,
  BlogDetailComments.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: async (state: AppState, dispatch: Dispatch<AnyAction>, args: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.init(apiInfo, { ...GET_COMMENTS_PAGE_ARGS, ...args, postId: args.postId });
  },
  mapResponse: (state: AppState, resp: BlogDetailComments.Api.InitResponse): InitSuccessActionData => ({
    ...resp,
    hasMore: resp.comments.length === GET_COMMENTS_PAGE_ARGS.size
  }),
  getShouldInit: (state: AppState, args: InitBeginActionData) => !!args.postId
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status === Status.None) {
    dispatch(_init({ ...args, page: 1, postId: state.page.postId! }));
  }
};

export type LoadMoreBeginActionData = EntityRef & {
  page: number;
  postId: string;
};
export type LoadMoreSuccessActionData = {
  comments: BlogDetailComments.PostComment[];
  hasMore: boolean;
};
export const _loadMore = apiAction<
  AppState,
  LoadMoreBeginActionData,
  BlogDetailComments.Api.LoadMoreResponse,
  LoadMoreSuccessActionData
>({
  types: {
    begin: Actions.LoadMoreBegin,
    failure: Actions.LoadMoreFailure,
    success: Actions.LoadMoreSuccess
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: LoadMoreBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.loadMore(apiInfo, { ...GET_COMMENTS_PAGE_ARGS, ...args });
  },
  mapResponse: (state: AppState, resp: BlogDetailComments.Api.LoadMoreResponse): LoadMoreSuccessActionData => ({
    comments: resp,
    hasMore: resp.length === GET_COMMENTS_PAGE_ARGS.size
  })
});

export type LoadMoreArgs = EntityRef & {};
export const loadMore = (args: LoadMoreArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status !== "LoadingMore") {
    dispatch(_loadMore({ ...args, postId: localState.postId, page: localState.page + 1 }));
  }
};
